// noinspection JSUnusedGlobalSymbols

import _ from 'lodash';
import Fuse from 'fuse.js';
import Moment from 'moment';
import shortHash from "shorthash2";
import CryptoJS from 'crypto-js';
import Numeral from 'numeral';
import mime from 'mime-types';
import {v7 as uuidv7} from 'uuid';

import {$logger, LogLevels} from "@isudio/globals";
import {MediaTypes, type NonEmptyArray, tagColors} from '../types/exports';

const isBoolean = (value: any) => {
  return (typeof value === 'boolean');
}

const isFunction = (value: any) => {
  return (typeof value === 'function');
}

const isNull = (value: any) => {
  return (value === null);
}

const isNumber = (value: any) => {
  return (typeof value === 'number');
}

const isObject = (value: any) => {
  return (value !== null && typeof value === 'object');
}

const isString = (value: any) => {
  return (typeof value === 'string');
}

const isUndefined = (value: any) => {
  return (value === undefined);
}

_.mixin({
  aesDecrypt(data: string, key: string): string {
    return CryptoJS.enc.Utf8.stringify(CryptoJS.AES.decrypt(data, key));
  }
});

_.mixin({
  aesEncrypt(data: string, key: string): any {
    if (_.isNothing(key)) {
      key = CryptoJS.SHA512(CryptoJS.lib.WordArray.random(32)).toString(CryptoJS.enc.Base64);
    }
    return {
      key: key,
      encrypted: CryptoJS.AES.encrypt(data, key).toString()
    };
  }
});

_.mixin({
  arrayMerge(oldArray: Array<any>, newArray: Array<any>, key?: any): any {
    if (_.isNothing(newArray)) {
      return null;
    }
    try {
      _.each(oldArray, (value: any, index) => {
        const type = typeof value;
        switch (type) {
          case 'object':
            const f = _.find(newArray, (o: any) => o[key] === value[key]);
            if (_.isNothing(f)) {
              oldArray.splice(index, 1);
            }
            break;

          default:
            if (_.indexOf(newArray, value) < 0) {
              oldArray.splice(index, 1);
            }
        }
      });

      _.each(newArray, (value) => {
        const type = typeof value;
        switch (type) {
          case 'object':
            const f = _.find(oldArray, o => o[key] === value[key]);
            if (_.isNothing(f)) {
              oldArray.push(value);
            } else {
              const i = _.findIndex(oldArray, o => o[key] === value[key]);
              _.oMerge(value, oldArray[i]);
            }
            break;

          default:
            if (_.indexOf(oldArray, value) < 0) {
              oldArray.push(value);
            } else {
              const i = _.findIndex(oldArray, value);
              oldArray[i] = value;
            }
        }
      });
      return oldArray;

    } catch (e) {
      _.printConsole(e);
      return null;
    }
  }
});

_.mixin({
  async asyncMap(array: any, func: any): Promise<any> {
    return Promise.all(array.map(func));
  }
});

_.mixin({
  async asyncMapSerial(array: any, func: any): Promise<any[]> {
    const results = [];
    for (const item of array) {
      results.push(await func(item));
    }
    return results;
  }
});

_.mixin({
  async asyncTimeout(fn = (): Promise<any> => { return Promise.resolve() }, ms: number): Promise<any> {
    return new Promise(async resolve => setTimeout(async () => {
      return resolve(await fn());
    }, ms));
  }
});

_.mixin({
  beginsWith(str: string, patterns: Array<any>, flag: string = 'i'): boolean {
    for (let i = 0; i < patterns.length; i++) {
      const r = new RegExp(patterns[i],  flag);
      const found =  r.test(str);
      if (found) return true;
    }
    return false;
  }
});

_.mixin({
  camelCaseToWords(camelCaseText: string): string {
    const result = camelCaseText.replace(/([A-Z])/g, " $1");
    return _.startCase(result);
  }
});

_.mixin({
  camelize(str: string, splitter: string = '_'): string {
    return _.split(str.toString().replace(/['\u2019]/g, ''), splitter).reduce((result, word, index) => {
      word = word.toLowerCase();
      return result + (index ? _.upperFirst(word) : word);
    }, '');
  }
});

_.mixin({
  clamp0To1(val: number): number {
    let y = Math.atan(val) / (Math.PI / 2);
    y = val / (1 + val);
    y = 1 - Math.exp(-val);
    return y;
  }
});

_.mixin({
  clampNumber(val: number, minVal: number = 0, maxVal: number = 25000, minClamp: number = 2, maxClamp: number = 50, roundTo: number = 0): number {
    let mx = (val - minVal) / (maxVal - minVal);
    let _nz = mx * (maxClamp - minClamp);
    return _.round((_nz + minClamp), roundTo);
  }
});

_.mixin({
  contains(str: string, patterns: Array<any>, caseSensitive: boolean = false): boolean {
    for (let i = 0; i < patterns.length; i++) {
      if (caseSensitive) {
        if (str.includes(patterns[i])) return true;
      } else {
        if (str.toLowerCase().includes(patterns[i].toLowerCase())) return true;
      }
    }
    return false;
  }
});

_.mixin({
  cropDecimal(point: number, dec: number = 6) {
    if (!isNumber(point)) {
      return 0;
    }
    let r = point.toString();
    return Number(r.substring(0, r.indexOf('.')) + r.substring(r.indexOf('.'), dec + 1));
  }
});

_.mixin({
  deepPick(obj: any, key: string, emptyVal?: any): any {
    try {
      if (!(obj instanceof HTMLElement)) {
        if (!_.has(obj, key)) {
          return (_.isUndefined(emptyVal) ? undefined : emptyVal);
        }
      }
      return _.get(obj, key, (_.isUndefined(emptyVal) ? undefined : emptyVal));
    } catch {
      return (_.isUndefined(emptyVal) ? undefined : emptyVal);
    }
  }
});

_.mixin({
  delay(wait: number, fn: Promise<any>): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(fn);
      }, wait)
    });
  }
});

_.mixin({
  deviceTime(utc: boolean = false): any {
    if (utc) {
      return Moment().utc().valueOf();
    } else {
      return Moment().format();
    }
  }
});

_.mixin({
  distanceTo(lat1: number, lon1: number, lat2: number, lon2: number, unit: string): number {
    let radlat1 = Math.PI * lat1 / 180;
    let radlat2 = Math.PI * lat2 / 180;
    let theta = lon1 - lon2;
    let radtheta = Math.PI * theta / 180;
    let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
    dist = Math.acos(dist);
    dist = dist * 180 / Math.PI;
    dist = dist * 60 * 1.1515;
    if (unit === 'K') {
      dist = dist * 1.609344;
    }
    if (unit === 'N') {
      dist = dist * 0.8684;
    }
    return dist;
  }
});

_.mixin({
  enumToObject(enumObj: any): Record<string, string | number> {
    return Object.keys(enumObj)
      .filter(key => isNaN(Number(key))) // Filter out reverse mappings for numeric enums
      .reduce((acc, key) => {
        acc[key] = (enumObj as any)[key];
        return acc;
      }, {} as Record<string, string | number>);
  }
});

_.mixin({
  filterKey(array, func, key) {
    let x = _.filter(array, func);
    if (x.length === 1) {
      return _.get(x[0], key, null);
    } else {
      return null;
    }
  }
});

_.mixin({
  format(num: number, format?: string): string {
    if (format) {
      return Numeral(num).format(format);
    } else {
      let result = _.replace(_.replace(_.replace(_.replace(Numeral(num).format(format || '0.0a'), new RegExp('.0k', 'g'), 'k'), new RegExp('.0m', 'g'), 'm'), new RegExp('.0b', 'g'), 'b'), new RegExp('.0t', 'g'), 't');
      return (_.endsWith(result, '.0') ? result.substring(0, result.lastIndexOf('.')) : result);
    }
  }
});

_.mixin({
  fromCharCode(n: number): string {
    return String.fromCharCode(n);
  }
});

_.mixin({
  fuseSearch(search: string, dataset: Array<any>, keys: Array<string>, options: any = {
    shouldSort: true,
    tokenize: true,
    matchAllTokens: true,
    findAllMatches: true,
    threshold: 0.3,
    location: 0,
    distance: 100,
    maxPatternLength: 32,
    minMatchCharLength: 1
  }): any {
    _.set(options, 'keys', keys);
    let fuse = new Fuse(dataset, options);
    return fuse.search(search);
  }
});

_.mixin({
  getArguments(func: any): any {
    let args = func.toString().match(/function\s.*?\(([^)]*)\)/)[1];

    return args.split(',').map((arg: any) => {
      return arg.replace(/\/\*.*\*\//, '').trim();
    }).filter((arg: any) => {
      return arg;
    });
  }
});

_.mixin({
  getDuplicatesByKey<T>(array: T[], key: keyof T): any[] {
    const keyCount = new Map<any, number>();

    array.forEach(item => {
      const keyValue = item[key];
      keyCount.set(keyValue, (keyCount.get(keyValue) || 0) + 1);
    });

    return Array.from(keyCount.entries())
      .filter(([_, count]) => count > 1)
      .map(([keyValue]) => keyValue);
  }
});

_.mixin({
  getErrKey(err, key) {
    let keys = ['name', 'code', 'message', 'stack', 'type', 'status'];
    if (_.isErrorObject(err)) {
      err = _.pick(err, keys);
    }

    return _.get(err, key);
  }
});

_.mixin({
  getFileExt(file) {
    const isWindows = /^win/.test(process.platform);
    const pathSeparator = (isWindows ? '\\' : '/');
    if (isString(file)) {
      return file.substring(file.lastIndexOf('.') + 1);

    } else {
      const filename = _.get(file, 'originalname');
      if (!_.isNothing(filename) && filename !== 'blob') {
        return filename.substring(filename.lastIndexOf('.') + 1);
      } else {
        if (!_.isNothing(_.get(file, 'mimetype'))) {
          return mime.extension(_.get(file, 'mimetype'));
        } else {
          const ext = file[_.get(file, 'type')].substring(file[_.get(file, 'type')].lastIndexOf(pathSeparator) + 1);
          if (!_.isNothing(ext)) {
            return ext;
          }
          throw new Error('Unable to determine file type!');
        }
      }
    }
  }
});

_.mixin({
  getFileName(file, separator = '/') {
    const f = file.substring((file.lastIndexOf(separator) + 1));
    return f.substring(0, f.lastIndexOf('.'));
  }
});

_.mixin({
  getFileNameWithExt(file) {
    const isWindows = /^win/.test(process.platform);
    const pathSeparator = (isWindows ? '\\' : '/');

    if (isString(file)) {
      return file.substring(file.lastIndexOf(pathSeparator) + 1);

    } else {
      if (!_.isNothing(_.get(file, 'path'))) {
        return file.path.substring((file.path.lastIndexOf(pathSeparator) + 1)) + '.' + file.type.substring(file.type.lastIndexOf('/') + 1);
      }
    }
    throw new Error('Unable to determine file name with extension!');
  }
});

_.mixin({
  getFilePath(file, separator = '/') {
    return file.substring(0, file.lastIndexOf(separator));
  }
});

_.mixin({
  getFileType(file) {
    if (isString(file)) {
      return file.substring(0, file.lastIndexOf('/'));

    } else {
      return file.type.substring(0, file.type.lastIndexOf('/'));
    }
  }
});

_.mixin({
  getMimeType(s: string): any {
    return mime.lookup(s);
  }
});

_.mixin({
  getMomentShorthand(o: any, k?: any) {
    const s = (_.isString(o) ? o : _.get(o, k));
    return _.first(s);
  }
});

_.mixin({
  getPatterns(str: string, pattern: any = /%%[\s\S]*?%%/g) {
    let i = [];
    let m = pattern.exec(str);
    while (m) {
      i.push({tag: m[0], index: m.index, length: m[0].length});
      m = pattern.exec(str);
    }
    return i;
  }
});

_.mixin({
  getRandom<T>(items: NonEmptyArray<T>): T {
    if (items.length === 1) return items[0];
    return items[_.getRandomInt(0, items.length - 1)] as unknown as T;
  }
});

_.mixin({
  getRandomInt(min: number, max: number): number {
    [min, max].forEach((v, idx) => {
      if (!Number.isSafeInteger(v)) {
        throw new TypeError(
          `${idx === 0 ? 'min' : 'max'} is not a valid integer`
        );
      }
    });
    if (max < min) {
      throw new Error('Min cannot be greater than max');
    }
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }
});

_.mixin({
  getValidationErrors(errors: Array<any>): any {
    const collection = _.flattenDeep(_.map(_.map(errors, v => v.constraints), o => _.map(o, v => JSON.parse(v))));
    return _.mapValues(_.keyBy(_.uniqBy(_.map(collection, o => {
      o.error = o.error + _.capitalize(o.attribute);
      return _.omit(o, ['constraint']);
    }), u => u.attribute), 'attribute'), 'error');
  }
});

_.mixin({
  hash(str: string): string {
    const sh: any = shortHash;
    return sh['unique'](str);
  }
});

_.mixin({
  ifEmpty(value, emptyVal) {
    return (_.isNothing(value) ? (!isUndefined(emptyVal) ? emptyVal : true) : (!isUndefined(value) ? value : false));
  }
});

_.mixin({
  initials(text: string, stringCase: string = 'upper'): string {
    const _case = (stringCase === 'upper' ? _.toUpper : _.toLower);
    const x: any = _.words(text);
    if (x.length > 1) {
      return _case(x[0].charAt(0) + x[1].charAt(0));
    } else {
      return _case(x[0].substring(0, 2));
    }
  }
});

_.mixin({
  insertString(str: string, index: number, substring: string): string {
    return [str.slice(0, index), substring, str.slice(index)].join('');
  }
});

_.mixin({
  isAudio(o: any): boolean {
    switch (typeof o) {
      case 'object':
        if (_.has(o, 'cdn')) {
          return _.startsWith(_.get(o, 'mimeType', null), MediaTypes.audio);
        }
        return (_.startsWith(_.get(o, 'type'), MediaTypes.audio) && (_.get(o, 'size', _.get(o, 'blob.size')) > 0));

      case 'string':
        const x: any = mime.lookup(o);
        return _.startsWith(x, MediaTypes.audio);
    }
    return false;
  }
});

_.mixin({
  isBehaviorSubject(o) {
    try {
      return o.constructor.name === 'BehaviorSubject';

    } catch (err) {
      return false;
    }
  }
});

_.mixin({
  isBrowser() {
    return (typeof window !== 'undefined');
  }
});

_.mixin({
  isEmptyValue(value: any): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    return typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.keys(value).length === 0;
  }
});

_.mixin({
  isErrorObject(o) {
    try {
      return o != null &&
        isObject(o) && !Array.isArray(o) &&
        ['[object Error]'].indexOf(Object.prototype.toString.call(o)) > -1 &&
        isFunction(o.constructor);

    } catch (err) {
      return false;
    }
  }
});

_.mixin({
  isEven(n: number): boolean {
    return n % 2 === 0;
  }
});

_.mixin({
  isGif(o: any): boolean {
    switch (typeof o) {
      case 'object':
        return _.endsWith(_.get(o, 'fileType', ''), MediaTypes.gif);

      case 'string':
        return _.endsWith(o, MediaTypes.gif);
    }
    return false;
  }
});

_.mixin({
  isGifAudio(o: any): boolean {
    if (_.isImage(o)) {
      return _.startsWith(_.get(o, 'audio.mimeType', null), MediaTypes.audio);
    }
    return false;
  }
});

_.mixin({
  isImage(o: any): boolean {
    switch (typeof o) {
      case 'object':
        if (_.has(o, 'cdn')) {
          return _.startsWith(_.get(o, 'mimeType', MediaTypes.image), MediaTypes.image);
        }
        return (_.startsWith(_.get(o, 'type'), MediaTypes.image) && (_.get(o, 'size', _.get(o, 'blob.size')) > 0));

      case 'string':
        const x: any = mime.lookup(o);
        return _.startsWith(x, MediaTypes.image);
    }
    return false;
  }
});

_.mixin({
  isJSONString(text: string): boolean {
    try {
      const o = JSON.parse(text);
      return (o && typeof o === 'object');

    } catch (_e) {
      return false;
    }
  }
});

_.mixin({
  isMedia(o: any): boolean {
    if (_.has(o, 'cdn')) {
      return _.indexOf([MediaTypes.media, MediaTypes.mediaThumbnail], (_.isObject(o) ? _.get(o, 'type') : o)) > -1;
    }
    return false;
  }
});

_.mixin({
  isNoImage(o: any): boolean {
    return _.get(o, 'key', null) === 'noImage';
  }
});

_.mixin({
  isNothing(value, keys) {
    let _val: any = value;
    if (!isUndefined(keys)) {
      _val = _.get(value, keys, null);
    }
    if (_.isErrorObject(_val)) {
      return false;
    }
    if (_.isArray(_val)) {
      return (_val.length === 0);
    }
    if (!isString(_val) && !isNumber(_val) && !isBoolean(_val)) {
      return _.isEmpty(_.cloneDeep(_val));
    }

    if (isNumber(_val) || isBoolean(_val)) {
      if (isNull(_val) || isUndefined(_val)) {
        return true;
      } else {
        return (_val.toString().length === 0);
      }
    }
    if (isString(_val)) {
      if (isNull(_val) || isUndefined(_val)) {
        return true;
      } else {
        return (_val.length === 0);
      }
    }
  }
});

_.mixin({
  isNumeric(n: number | string): boolean {
    return (_.isNumber(n) && !isNaN(Number(n)));
  }
});

_.mixin({
  isObservable(o) {
    try {
      return o.constructor.name === 'Observable';

    } catch (err) {
      return false;
    }
  }
});

_.mixin({
  isPlainObject(o) {
    try {
      return o != null &&
        isObject(o) && !Array.isArray(o) &&
        ['[object Object]'].indexOf(Object.prototype.toString.call(o)) > -1 &&
        isFunction(o.constructor) &&
        ['[object Object]'].indexOf(Object.prototype.toString.call(o.constructor.prototype)) > -1;

    } catch (err) {
      return false;
    }
  }
});

_.mixin({
  isPromise(o) {
    return (o instanceof Promise);
  }
});

_.mixin({
  isUrl(s: string): boolean {
    return _.startsWith(s, 'http');
  }
});

_.mixin({
  isVideo(o: any): boolean {
    switch (typeof o) {
      case 'object':
        if (_.has(o, 'cdn')) {
          return _.startsWith(_.get(o, 'mimeType', null), MediaTypes.video);
        }
        return (_.startsWith(_.get(o, 'type'), MediaTypes.video) && (_.get(o, 'size', _.get(o, 'blob.size')) > 0));

      case 'string':
        const x: any = mime.lookup(o);
        return _.startsWith(x, MediaTypes.video);
    }
    return false;
  }
});

_.mixin({
  JtoS(obj = {}, keys?) {
    if (!keys) {
      keys = null;
    }
    if (!_.isNothing(_.property('stack')(obj))) {
      return _.getErrKey(obj, 'stack').unescapeHTML();
    }
    return JSON.stringify(obj, keys, 4);
  }
});

_.mixin({
  JtoV(obj, keys?) {
    if (!keys) {
      keys = null;
    }

    switch (typeof obj) {
      case 'object':
        return JSON.parse(JSON.stringify(obj, keys));

      case 'string':
        return JSON.parse(obj);
    }
  }
});

_.mixin({
  logDebug(log: any, data?: any): void {
    if (_.isNothing(log) && _.isDebug()) return;
    if (!_.isString(log) && _.isNothing(data)) {
      $logger.write('', LogLevels.debug, log);
    } else {
      $logger.write(log, LogLevels.debug, data);
    }
  }
});

_.mixin({
  logError(log: any, err?: any): void {
    if (_.isNothing(log)) return;

    if (_.isError(log)) {
      $logger.write(err.message, LogLevels.error, err);

    } else {
      if (_.isString(log)) {
        $logger.write(log, LogLevels.error, err);
      }
    }
  }
});

_.mixin({
  logEvent(log: string, data?: any): void {
    if (_.isNothing(log)) return;
    $logger.write(log, LogLevels.event, data);
  }
});

_.mixin({
  logInfo(log: any, data?: any): void {
    if (_.isNothing(log) || !_.isDebug()) return;
    if (!_.isString(log) && _.isNothing(data)) {
      $logger.write('', LogLevels.info, log);
    } else {
      $logger.write(log, LogLevels.info, data);
    }
  }
});

_.mixin({
  logWarning(log: any, err?: any): void {
    if (_.isNothing(log)) return;

    if (_.isError(log)) {
      $logger.write(err.message, LogLevels.warn, err);

    } else {
      if (_.isString(log)) {
        $logger.write(log, LogLevels.warn, err);
      }
    }
  }
});

_.mixin({
  move(array: Array<any>, from: number, to: number): Array<any> {
    const _array = _.cloneDeep(array);
    const item = _array[from];
    _array.splice(from, 1);
    _array.splice(to, 0, item);
    return _array;
  }
});

_.mixin({
  newGuid: (): any => {
    let d = _.deviceTime(true);
    let d2 = (performance && performance.now && (performance.now() * 1000)) || 0;
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      let r = Math.random() * 16;
      if (d > 0) {
        r = (d + r) % 16 | 0;
        d = Math.floor(d / 16);
      } else {
        r = (d2 + r) % 16 | 0;
        d2 = Math.floor(d2 / 16);
      }
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }
});

_.mixin({
  oGet(o: any, k: string, e: any, parse: boolean = false) {
    try {
      const val = _.get(o, k, null);

      if (_.isNothing(val)) {
        return (isUndefined(e) ? undefined : e);

      } else {
        if (parse && _.isString(val)) {
          return JSON.parse(val);
        } else {
          return val;
        }
      }

    } catch {
      return (isUndefined(e) ? undefined : e);
    }
  }
});

_.mixin({
  oMerge(oNew: any, oOld: any = []): any {
    if (_.isNothing(oNew) || _.isNothing(oOld)) {
      return null;
    }
    try {
      const nKeys = _.keys(oNew);
      const oKeys = _.keys(oOld);
      _.each(nKeys, k => {
        if (!_.isEqual(oOld[k], oNew[k])) {
          if (_.isArray(oNew[k])) {
            if (_.isNothing(oOld[k])) {
              oOld[k] = [];
            }
            if (_.get(oOld[k], `length`, 0) > oNew[k].length) {
              if (!_.isNothing(oOld[k])) {
                oOld[k].splice(0, (oOld[k].length - oNew[k].length));
              }
            }
            _.each(oNew[k], (_v, i) => {
              if (i < _.get(oOld[k], `length`, 0)) {
                if (!_.isEqual(oNew[k][i], oOld[k][i])) {
                  _.oMerge(oNew[k][i], oOld[k][i]);
                }
              } else {
                oOld[k].push(oNew[k][i]);
              }
            });
          } else {
            if (!_.isNothing(oNew[k])) {
              oOld[k] = oNew[k];
            }
          }
        }
      });
      _.each(oKeys, k => {
        if (!_.omitStartsWith(k)) {
          if (!_.has(oNew, k)) {
            delete oOld[k];
          }
        }
      });
      return oOld;
    } catch {
      return null;
    }
  }
});

_.mixin({
  omitStartsWith(key: string, startsWith: string = '_'): boolean {
    return ((_.indexOf(['_id', '_rev'], key) < 0) && (_.startsWith(key, startsWith)));
  }
});

_.mixin({
  parse(value: any) {
    let o: any;
    try {
      if (isString(value)) {
        o = JSON.parse(value);
      } else {
        o = _.toPlainObject(value);
      }
    } catch (err) {
      o = value;
    }
    return o;
  }
});

_.mixin({
  printConsole(result: any, stringify: boolean = false) {
    let r: any;
    if (isObject(result)) {
      if (_.has(result, 'rows')) {
        r = _.map(result['rows'], 'doc');
      } else {
        r = result;
      }
      if (stringify) {
        r = _.JtoS(r);
      }

    } else {
      r = result;
    }
    console.log(r);
    return r;
  }
});

_.mixin({
  removeArrayItem<T>(arr: T[], item: T): T[] {
    const index = arr.indexOf(item);
    if (index !== -1) {
      arr.splice(index, 1);
    }
    return arr;
  }
});

_.mixin({
  removeDuplicatesByKey<T>(array: T[], key: keyof T): T[] {
    return [...new Map(array.map(item => [item[key], item]))].map(([_, value]) => value);
  }
});

_.mixin({
  removeEmptyKeys (obj: any): any {
    if (typeof obj !== 'object' || obj === null) return obj;

    const result: any = Array.isArray(obj) ? [] : {};

    for (const [key, value] of Object.entries(obj)) {
      let cleaned = value;
      if (typeof value === 'object' && value !== null) {
        cleaned = _.removeEmptyKeys(value);
      }
      if (!_.isEmptyValue(cleaned)) {
        result[key] = cleaned;
      }
    }

    return result;
  }
});

_.mixin({
  replaceAll(str: string, find: string, replace: string): string {
    return (_.replace(str, new RegExp(find, 'g'), replace));
  }
});

_.mixin({
  roundNearest(value: number, nearest: number = 5, decimal: number = 0): number {
    return _.round((Math.ceil(value / nearest) * nearest), decimal);
  }
});

_.mixin({
  sha512(str, enc: any = CryptoJS.enc.Base64) {
    return CryptoJS.SHA512(str).toString(enc);
  }
});

_.mixin({
  shortID(str, hash = true) {
    const sha512 = _.sha512(str);
    return (hash ? shortHash(sha512) : sha512);
  }
});

_.mixin({
  shuffle(array: Array<any>, clone: boolean = true): Array<any> {
    const ary: Array<any> = (clone ? _.cloneDeep(array) : array);
    for (let i = ary.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [ary[i], ary[j]] = [ary[j], ary[i]];
    }
    return ary;
  }
});

_.mixin({
  strToNum(str) {
    let encode = '';
    for (let i = 0; i < str.length; i++) {
      let x = str.slice(i, i + 1);
      encode += x.charCodeAt(0);

    }
    return encode;
  }
});

_.mixin({
  strip(str: string, charsRegex = new RegExp(/[ `~!@#$%^&*()_|+\-=?;:'",.<>{}\[\]\\/]/gi)) {
    return _.trim(_.toLower(_.replace(str, charsRegex, '')));
  }
});

_.mixin({
  tagColor(t: string): string {
    const tag: any = _.toLower(_.get(t, 'tag', t));
    const search: string = _.isNumeric(_.first(tag)) ? 'a' : _.first(tag);
    const f: any = _.find(tagColors, (v) => {
      return _.startsWith(search, Object.keys(v)[0]);
    });
    return _.get(f, search, 'var(--app-white-light)');
  }
});

_.mixin({
  toBoolean(value: string) {
    return (value === 'true');
  }
});

_.mixin({
  toEnum(arr: string[]): Record<string, string> {
    return Object.fromEntries(arr.map(key => [key, key]));
  }
});

_.mixin({
  until(run: any, predicate: any): any {
    let method = (subject: any) => {
      if (!predicate(subject)) {
        return method(run(subject));
      } else {
        return subject;
      }
    };
    return method;
  }
});

_.mixin({
  uuid(buffer: boolean = false): any {
    if (!buffer) {
      return uuidv7();

    } else {
      const randomBytes = new Uint8Array(16);
      crypto.getRandomValues(randomBytes);
      return uuidv7({random: randomBytes});
    }
  }
});

_.mixin({
  when(timestamp: number): string | null {
    try {
      if (_.toString(timestamp).length < 13) {
        return Moment.unix(timestamp).fromNow();
      } else {
        return Moment.utc(timestamp).fromNow();
      }
    } catch {
      return null;
    }
  }
});

export default _;